import { findRequestId } from '../functions/findRequestId';
import { ErrorReportingClient } from '../types/ErrorReportingClient';
import { ErrorReportingDevtools } from '../types/ErrorReportingDevtools';
import { ErrorReportingStateDependencies } from '../types/ErrorReportingStateDependencies';
import { HttpClientError } from '../types/HttpClientError';

const DEFAULT_PATH = 'error-reporting-devtools/not-found';

/**
 * Makes a real request to the Consumer Portal API that fails (by default a
 * 404 for a route that does not exist) and captures the resulting axios
 * error. Unlike `simulateNetworkError`, the response is real, so the event
 * carries the API's `X-Request-Id` as its `requestId` tag - provided CORS
 * exposes the header - and the id finds the request's logs in Sumo Logic.
 * Resolves with the request id, or `undefined` if none was readable.
 */
export const createTriggerApiErrorDevtool = (
  client: ErrorReportingClient,
  { useApiStore }: ErrorReportingStateDependencies,
): ErrorReportingDevtools['triggerApiError'] => {
  return async (path = DEFAULT_PATH): Promise<string | undefined> => {
    try {
      await useApiStore.getState().portalApi().get(path);
    } catch (error) {
      const requestId = findRequestId(
        (error as HttpClientError).response?.headers,
      );

      client.captureException(error, {
        extra: { source: 'errorReporting.triggerApiError' },
        tags: { triggered: 'true', triggeredKind: 'api-error' },
      });

      if (requestId) {
        console.info(
          `errorReporting.triggerApiError: captured the failed request with X-Request-Id ${requestId}`,
        );
      } else {
        console.warn(
          'errorReporting.triggerApiError: captured the failed request, but no X-Request-Id was readable. Check that the API lists it in Access-Control-Expose-Headers.',
        );
      }

      return requestId;
    }

    console.warn(
      `errorReporting.triggerApiError: GET ${path} succeeded, so nothing was captured. Pass a path that fails.`,
    );

    return undefined;
  };
};
